import { combineReducers } from "redux";
import { apiReducer } from '../../utils/api/reducer';
import { Constants } from './constants';
import { SitesResponse, SlateResponse, SportsResponse } from "../../common/store/types";
import { ResultsLoadError, SimulationTimestamp } from "./types";

export const selectedContest = (state = {} as SitesResponse, action: any) => {
  switch (action.type) {
    case Constants.SET_CONTEST:
      return action.payload;
    default:
      return state;
  }
};

export const selectedSport = (state = {} as SportsResponse, action: any) => {
  switch (action.type) {
    case Constants.SET_SPORT:
      return action.payload;
    default:
      return state;
  }
};

export const selectedSite = (state = {} as SitesResponse, action: any) => {
  switch (action.type) {
    case Constants.SET_SITE:
      return action.payload;
    default:
      return state;
  }
};

export const selectedSlate = (state = {} as SlateResponse, action: any) => {
  switch (action.type) {
    case Constants.SET_SLATE:
      return action.payload;
    default:
      return state;
  }
};

export const lineupVerified = (state = false, action: any) => {
  switch (action.type) {
    case Constants.SET_LINEUP_VERIFIED:
      return action.payload;
    default:
      return state;
  }
}

export const simulatedSlate = (state = {} as SlateResponse, action: any) => {
  switch (action.type) {
    case Constants.SET_SIMULATED_SLATE:
      return action.payload;
    default:
      return state;
  }
};

const initialPageState = {
  player: {
    pageSize: 150,
    currentPage: 1,
  },
  lineup: {
    pageSize: 150,
    currentPage: 1,
  },
  stack: {
    pageSize: 150,
    currentPage: 1,
  },
}

export const pageInfo = (state = initialPageState, action: any) => {
  switch (action.type) {
    case Constants.SET_PAGE:
      return {
        ...action.payload
      };
    default:
      return state;
  }
};

const initialFilterState = {
  player: {
    all: {
      sortBy: 'SIMROI',
      sortOrder: 'DESC'
    },
  },
  lineup: {
    all: {
      sortBy: 'SIMROI',
      sortOrder: 'DESC'
    },
  },
  stack: {
    all: {
      sortBy: 'TEAM',
      sortOrder: 'DESC'
    },
  },
}

export const filters = (state = initialFilterState, action: any) => {
  switch (action.type) {
    case Constants.SET_USER_SORT:
      return {
        ...state,
        user: { ...action.payload }
      }
    case Constants.SET_PLAYER_SORT:
      return {
        ...state,
        player: { ...action.payload }
      }
    case Constants.SET_LINEUP_SORT:
      return {
        ...state,
        lineup: { ...action.payload }
      }
    case Constants.SET_STACK_SORT:
      return {
        ...state,
        stack: { ...action.payload }
      }
    default:
      return state;
  }
}

export const disableProgressCheck = (state = true, action: any) => {
  switch (action.type) {
    case Constants.SET_DISABLE_PROGRESS_CHECK:
      return action.payload;
    default:
      return state;
  }
}

const initialPaginationState = {
  player: {
    all: {
      currentPage: 0,
      nextPageLink: "",
      pageSize: 0,
      previousPageLink: null,
      totalCount: 0,
      totalPages: 0,
    },
  },
  lineup: {
    all: {
      currentPage: 0,
      nextPageLink: "",
      pageSize: 0,
      previousPageLink: null,
      totalCount: 0,
      totalPages: 0,
    },
  },
  stack: {
    all: {
      currentPage: 0,
      nextPageLink: "",
      pageSize: 0,
      previousPageLink: null,
      totalCount: 0,
      totalPages: 0,
    },
  },
};

export const pagination = (state = initialPaginationState, action: any) => {
  switch (action.type) {
  case Constants.SET_PLAYER_ALL_ROI_PAGINATION:
      return {
        ...state,
        player: {
          ...state.player,
            all: action.payload
        }
      }
  case Constants.SET_LINEUP_ALL_ROI_PAGINATION:
      return {
        ...state,
        lineup: {
          ...state.lineup,
            all: action.payload
        }
      }
  case Constants.SET_STACK_ALL_ROI_PAGINATION:
      return {
        ...state,
        stack: {
          ...state.stack,
            all: action.payload
        }
      }
    default:
      return state;
  }
}

export const totalEntries = (state = 0, action: any) => {
  switch (action.type) {
    case Constants.SET_TOTAL_ENTRIES:
      return action.payload;
    default:
      return state;
  }
}

export const lineupFile = (state = '', action: any) => {
  switch (action.type) {
    case Constants.SET_LINEUP_FILE:
      return action.payload;
    default:
      return state;
  }
}

export const percentToFirst = (state = 25, action: any) => {
  switch (action.type) {
    case Constants.SET_PERCENT_TO_FIRST:
      return action.payload;
    default:
      return state;
  }
}

export const simCount = (state = 0, action: any): number => {
  switch (action.type) {
    case Constants.SET_SIM_COUNT:
      return action.payload;
    default:
      return state;
  }

}

export const simulationTimestamp = (state: SimulationTimestamp = { startTime: null }, action: any): SimulationTimestamp => {
  switch (action.type) {
    case Constants.SET_SIMULATION_TIMESTAMP:
      return action.payload;
    default:
      return state;
  }
}

export const showCachedResultError = (state = false, action: any): boolean => {
  switch (action.type) {
    case Constants.SET_SHOW_CACHED_RESULT_ERROR:
      return action.payload;
    default:
      return state;
  }
}

export const resultsLoadError = (state: ResultsLoadError | null = null, action: any): ResultsLoadError | null => {
  switch (action.type) {
    case Constants.SET_RESULTS_LOAD_ERROR:
      return action.payload;
    default:
      return state;
  }
}

export const isParsingCsv = (state = false, action: any): boolean => {
  switch (action.type) {
    case Constants.SET_IS_PARSING_CSV:
      return action.payload;
    default:
      return state;
  }
}

export const preContestReducer = combineReducers({
  selectedContest,
  pageInfo,
  pagination,
  filters,
  disableProgressCheck,
  selectedSport,
  selectedSite,
  selectedSlate,
  simulatedSlate,
  totalEntries,
  lineupFile,
  percentToFirst,
  lineupVerified,
  simCount,
  simulationTimestamp,
  showCachedResultError,
  resultsLoadError,
  isParsingCsv,
  playerAllRoi: apiReducer(Constants.SET_PLAYER_ALL_ROI),
  lineupAllRoi: apiReducer(Constants.SET_LINEUP_ALL_ROI),
  stackRoi: apiReducer(Constants.SET_STACK_ALL_ROI),
  contestPlayers: apiReducer(Constants.SET_CONTEST_PLAYERS),
  supportedSites: apiReducer(Constants.SET_SITES),
});